import type {
  AlertsResponse,
  AnticipationResponse,
  AppLoginResponse,
  AppMeResponse,
  CrossingResponse,
  Deviation,
  DeviationAckRequest,
  DeviationAckResponse,
  DeviationsResponse,
  Direction,
  HandoverResponse,
  HeadwaysResponse,
  Journey,
  JourneyDetail,
  JourneysResponse,
  Junction,
  JunctionAlert,
  JunctionStatusPatch,
  KpiResponse,
  Line,
  LineHealth,
  LineShapeResponse,
  LineStop,
  MapStop,
  NetworkConfigResponse,
  OpaqueId,
  PunctualityDaysResponse,
  PunctualityReport,
  PunctualityMonthlyResponse,
  VehiclesResponse,
  VehicleGanttResponse,
  VehicleAgendaResponse,
  ReplayResponse,
  ReplayBoundsResponse,
  ActiveServicesResponse,
  ServicePlanResponse,
  ServicesTimelineResponse,
  EndOfServiceResponse,
  RegRoute,
  RegulatorAuditResponse,
  Session,
  SearchResponse,
  RegulationOptionsResponse,
  SessionRequest,
  SourcesHealthResponse,
  SortieReallocationResponse,
  SpacetimeResponse,
  SpacetimeWindow,
  SynopticResponse,
  ThermometerResponse,
  TurnbackOptionsResponse,
  NextPassagesResponse,
} from "../contract/types";
import { HttpClient, type HttpClientOptions } from "./http";

export interface NetworkSummary {
  id: OpaqueId;
  name: string;
  enabled: boolean;
}

export interface RegulationPost {
  id: OpaqueId;
  label: string;
  operatorIds: string[];
  /** Phase 2 (contrat v1.12) — exploitants opérationnels du poste (« P4 = tout TNI »). */
  exploitantIds: string[];
  /** Lignes du poste, **déjà résolues serveur** (union des `includes`, lignes des exploitants). */
  lineIds: OpaqueId[];
}

export interface JourneyQuery {
  post?: OpaqueId | undefined;
  lineIds?: OpaqueId[] | undefined;
  bbox?: readonly [number, number, number, number] | undefined;
  positionTypes?: string[] | undefined;
}

/**
 * Client d'API Sentinelle (contrat v1.2). Tout est scopé par réseau, sauf
 * `/networks`, `/auth/session`, `/health`. **Lecture seule**, hormis les quatre
 * écritures autorisées : `postSession`, `acknowledgeDeviations`,
 * `patchJunctionStatus`, et `/admin/**` (non couvert ici).
 *
 * Les méthodes déballent l'enveloppe `{ x: [...] }` quand elle ne porte que la
 * liste ; elles gardent l'enveloppe quand elle porte un `seq`/`at`/`truncated`
 * dont l'appelant a besoin (courses, écarts, alertes, santé des sources).
 */
export class SentinelleApi {
  readonly http: HttpClient;

  constructor(options: HttpClientOptions | HttpClient) {
    this.http = options instanceof HttpClient ? options : new HttpClient(options);
  }

  private base(networkId: OpaqueId): string {
    return `/networks/${encodeURIComponent(networkId)}`;
  }

  // ── Non scopés ────────────────────────────────────────────────────────────
  postSession(request: SessionRequest, signal?: AbortSignal): Promise<Session> {
    return this.http.post<Session>("/auth/session", { body: request, ...(signal ? { signal } : {}) });
  }

  // ── Porte d'accès applicative (login principal, contrat v1.38 §8bis) ──────
  // Régime distinct de `postSession` : la session vit dans le cookie `HttpOnly`
  // `sentinelle_session` posé par le backend — rien à porter ici.

  /**
   * État de la porte au démarrage : `{ username, role }` si le cookie est
   * valide, sinon `401`. `role: "admin"` (v1.39) ⇒ ce même cookie ouvre déjà
   * `/admin/**` — lu par `AdminScreen` pour éviter un second login.
   */
  authMe(signal?: AbortSignal): Promise<AppMeResponse> {
    return this.http.get<AppMeResponse>("/auth/me", signal ? { signal } : {});
  }

  /** Connexion. Succès → le backend pose le cookie de session ; `401` (message unique) ou `422`. */
  appLogin(username: string, password: string, signal?: AbortSignal): Promise<AppLoginResponse> {
    return this.http.post<AppLoginResponse>("/auth/login", {
      body: { username, password },
      ...(signal ? { signal } : {}),
    });
  }

  /** Déconnexion : révoque la session serveur et vide le cookie (`204`). */
  async appLogout(signal?: AbortSignal): Promise<void> {
    await this.http.post<void>("/auth/logout", signal ? { signal } : {});
  }

  async listNetworks(signal?: AbortSignal): Promise<NetworkSummary[]> {
    const r = await this.http.get<{ networks: NetworkSummary[] }>("/networks", signal ? { signal } : {});
    return r.networks;
  }

  health(signal?: AbortSignal): Promise<{ status: string }> {
    return this.http.get<{ status: string }>("/health", signal ? { signal } : {});
  }

  // ── §2 Configuration ─────────────────────────────────────────────────────
  getConfig(networkId: OpaqueId, signal?: AbortSignal): Promise<NetworkConfigResponse> {
    return this.http.get<NetworkConfigResponse>(`${this.base(networkId)}/config`, signal ? { signal } : {});
  }

  async listPosts(networkId: OpaqueId, signal?: AbortSignal): Promise<RegulationPost[]> {
    const r = await this.http.get<{ posts: RegulationPost[] }>(
      `${this.base(networkId)}/posts`,
      signal ? { signal } : {},
    );
    return r.posts;
  }

  async listLines(networkId: OpaqueId, signal?: AbortSignal): Promise<Line[]> {
    const r = await this.http.get<{ lines: Line[] }>(`${this.base(networkId)}/lines`, signal ? { signal } : {});
    return r.lines;
  }

  getLineShape(
    networkId: OpaqueId,
    lineId: OpaqueId,
    direction: Direction,
    signal?: AbortSignal,
  ): Promise<LineShapeResponse> {
    return this.http.get<LineShapeResponse>(`${this.base(networkId)}/lines/${encodeURIComponent(lineId)}/shape`, {
      query: { direction },
      ...(signal ? { signal } : {}),
    });
  }

  async getLineStops(
    networkId: OpaqueId,
    lineId: OpaqueId,
    direction: Direction,
    signal?: AbortSignal,
  ): Promise<LineStop[]> {
    const r = await this.http.get<{ stops: LineStop[] }>(
      `${this.base(networkId)}/lines/${encodeURIComponent(lineId)}/stops`,
      { query: { direction }, ...(signal ? { signal } : {}) },
    );
    return r.stops;
  }

  async getLinesHealth(
    networkId: OpaqueId,
    opts: { post?: OpaqueId } = {},
    signal?: AbortSignal,
  ): Promise<LineHealth[]> {
    const r = await this.http.get<{ lines: LineHealth[] }>(`${this.base(networkId)}/lines/health`, {
      query: { post: opts.post },
      ...(signal ? { signal } : {}),
    });
    return r.lines;
  }

  /** Recherche unifiée de la loupe carte (J.4) — véhicule / carrefour / arrêt. */
  async search(networkId: OpaqueId, q: string, signal?: AbortSignal): Promise<SearchResponse> {
    return this.http.get<SearchResponse>(`${this.base(networkId)}/search`, {
      query: { q },
      ...(signal ? { signal } : {}),
    });
  }

  // ── §3 Courses actives ───────────────────────────────────────────────────
  getJourneys(
    networkId: OpaqueId,
    query: JourneyQuery = {},
    signal?: AbortSignal,
  ): Promise<JourneysResponse> {
    return this.http.get<JourneysResponse>(`${this.base(networkId)}/journeys`, {
      query: {
        post: query.post,
        lines: query.lineIds,
        bbox: query.bbox ? [...query.bbox] : undefined,
        positionTypes: query.positionTypes,
      },
      ...(signal ? { signal } : {}),
    });
  }

  getJourney(networkId: OpaqueId, journeyId: OpaqueId, signal?: AbortSignal): Promise<JourneyDetail> {
    return this.http.get<JourneyDetail>(
      `${this.base(networkId)}/journeys/${encodeURIComponent(journeyId)}`,
      signal ? { signal } : {},
    );
  }

  // ── §4 Cockpit ───────────────────────────────────────────────────────────
  getDeviations(
    networkId: OpaqueId,
    opts: { post?: OpaqueId; lineId?: OpaqueId } = {},
    signal?: AbortSignal,
  ): Promise<DeviationsResponse> {
    return this.http.get<DeviationsResponse>(`${this.base(networkId)}/deviations`, {
      query: { post: opts.post, line: opts.lineId },
      ...(signal ? { signal } : {}),
    });
  }

  /** Seule écriture du cockpit. L'auteur enregistré est le poste, pas la personne. */
  acknowledgeDeviations(
    networkId: OpaqueId,
    request: DeviationAckRequest,
    signal?: AbortSignal,
  ): Promise<DeviationAckResponse> {
    return this.http.post<DeviationAckResponse>(`${this.base(networkId)}/deviations/ack`, {
      body: request,
      ...(signal ? { signal } : {}),
    });
  }

  getSpacetime(
    networkId: OpaqueId,
    params: { lineId: OpaqueId; direction: Direction; window: SpacetimeWindow },
    signal?: AbortSignal,
  ): Promise<SpacetimeResponse> {
    return this.http.get<SpacetimeResponse>(`${this.base(networkId)}/spacetime`, {
      query: { line: params.lineId, direction: params.direction, window: params.window },
      ...(signal ? { signal } : {}),
    });
  }

  getHeadways(
    networkId: OpaqueId,
    params: { lineId: OpaqueId; direction: Direction },
    signal?: AbortSignal,
  ): Promise<HeadwaysResponse> {
    return this.http.get<HeadwaysResponse>(`${this.base(networkId)}/headways`, {
      query: { line: params.lineId, direction: params.direction },
      ...(signal ? { signal } : {}),
    });
  }

  getKpi(networkId: OpaqueId, opts: { post?: OpaqueId } = {}, signal?: AbortSignal): Promise<KpiResponse> {
    return this.http.get<KpiResponse>(`${this.base(networkId)}/kpi`, {
      query: { post: opts.post },
      ...(signal ? { signal } : {}),
    });
  }

  getSynoptic(networkId: OpaqueId, opts: { post?: OpaqueId } = {}, signal?: AbortSignal): Promise<SynopticResponse> {
    return this.http.get<SynopticResponse>(`${this.base(networkId)}/synoptic`, {
      query: { post: opts.post },
      ...(signal ? { signal } : {}),
    });
  }

  // ── §5 Carte pilote ──────────────────────────────────────────────────────
  getAlerts(networkId: OpaqueId, signal?: AbortSignal): Promise<AlertsResponse> {
    return this.http.get<AlertsResponse>(`${this.base(networkId)}/alerts`, signal ? { signal } : {});
  }

  async getStops(
    networkId: OpaqueId,
    opts: { bbox?: readonly [number, number, number, number] } = {},
    signal?: AbortSignal,
  ): Promise<MapStop[]> {
    const r = await this.http.get<{ stops: MapStop[] }>(`${this.base(networkId)}/stops`, {
      query: { bbox: opts.bbox ? [...opts.bbox] : undefined },
      ...(signal ? { signal } : {}),
    });
    return r.stops;
  }

  async listJunctions(networkId: OpaqueId, opts: { q?: string } = {}, signal?: AbortSignal): Promise<Junction[]> {
    const r = await this.http.get<{ junctions: Junction[] }>(`${this.base(networkId)}/junctions`, {
      query: { q: opts.q },
      ...(signal ? { signal } : {}),
    });
    return r.junctions;
  }

  /** Quatrième écriture de l'API : marqueur d'observation de carrefour, partagé entre postes (§12.5). */
  patchJunctionStatus(
    networkId: OpaqueId,
    junctionId: OpaqueId,
    patch: JunctionStatusPatch,
    signal?: AbortSignal,
  ): Promise<Junction> {
    return this.http.patch<Junction>(
      `${this.base(networkId)}/junctions/${encodeURIComponent(junctionId)}/status`,
      { body: patch, ...(signal ? { signal } : {}) },
    );
  }

  async getJunctionAlerts(networkId: OpaqueId, signal?: AbortSignal): Promise<JunctionAlert[]> {
    const r = await this.http.get<{ alerts: JunctionAlert[] }>(
      `${this.base(networkId)}/junctions/alerts`,
      signal ? { signal } : {},
    );
    return r.alerts;
  }

  async listRegRoutes(networkId: OpaqueId, opts: { lineId?: OpaqueId } = {}, signal?: AbortSignal): Promise<RegRoute[]> {
    const r = await this.http.get<{ regRoutes: RegRoute[] }>(`${this.base(networkId)}/reg-routes`, {
      query: { line: opts.lineId },
      ...(signal ? { signal } : {}),
    });
    return r.regRoutes;
  }

  // ── §6 Aide à la décision ────────────────────────────────────────────────
  /** Couvre toujours les deux sens dans le même appel (§12.2) — pas de paramètre `direction`. */
  getThermometer(networkId: OpaqueId, params: { lineId: OpaqueId }, signal?: AbortSignal): Promise<ThermometerResponse> {
    return this.http.get<ThermometerResponse>(`${this.base(networkId)}/thermometer`, {
      query: { line: params.lineId },
      ...(signal ? { signal } : {}),
    });
  }

  /**
   * Croisement de deux courses. Les paramètres `vehicleA`/`vehicleB` du contrat
   * prennent des **`journeyId`** (le serveur travaille sur les `nextStops` de
   * chaque course, pas sur un registre d'identité véhicule).
   */
  getCrossing(
    networkId: OpaqueId,
    journeyA: OpaqueId,
    journeyB: OpaqueId,
    signal?: AbortSignal,
  ): Promise<CrossingResponse> {
    return this.http.get<CrossingResponse>(`${this.base(networkId)}/crossing`, {
      query: { vehicleA: journeyA, vehicleB: journeyB },
      ...(signal ? { signal } : {}),
    });
  }

  getTurnbackOptions(
    networkId: OpaqueId,
    journeyId: OpaqueId,
    signal?: AbortSignal,
  ): Promise<TurnbackOptionsResponse> {
    return this.http.get<TurnbackOptionsResponse>(
      `${this.base(networkId)}/journeys/${encodeURIComponent(journeyId)}/turnback-options`,
      signal ? { signal } : {},
    );
  }

  /**
   * Proposition de régulation (P2-18, contrat v1.23 ; chiffrage v1.44) — le
   * drill-in par course du panneau « Aide à la décision » : toutes les
   * manœuvres, pas seulement la meilleure (`GET /anticipation` n'expose que
   * `plan.best`). Sentinelle conseille, n'agit pas — `GET` pur.
   */
  getRegulationOptions(
    networkId: OpaqueId,
    journeyId: OpaqueId,
    signal?: AbortSignal,
  ): Promise<RegulationOptionsResponse> {
    return this.http.get<RegulationOptionsResponse>(
      `${this.base(networkId)}/journeys/${encodeURIComponent(journeyId)}/regulation-options`,
      signal ? { signal } : {},
    );
  }

  /**
   * Prochain passage (P2-16, contrat v1.15). `stop` seul → prochains véhicules à
   * l'arrêt (ordre chronologique) ; `+ vehicle` → prochain passage de ce n° de
   * parc. Outil de consultation — aucune exclusion de périmètre décisionnel.
   */
  getNextPassages(
    networkId: OpaqueId,
    query: { stopId: OpaqueId; vehicle?: string; lineId?: OpaqueId; limit?: number },
    signal?: AbortSignal,
  ): Promise<NextPassagesResponse> {
    return this.http.get<NextPassagesResponse>(`${this.base(networkId)}/next-passages`, {
      query: { stop: query.stopId, vehicle: query.vehicle, line: query.lineId, limit: query.limit },
      ...(signal ? { signal } : {}),
    });
  }

  // ── §7 Santé des sources ─────────────────────────────────────────────────
  getSourcesHealth(networkId: OpaqueId, signal?: AbortSignal): Promise<SourcesHealthResponse> {
    return this.http.get<SourcesHealthResponse>(`${this.base(networkId)}/sources/health`, signal ? { signal } : {});
  }

  // ── §12 Observabilité (écrans différés — client complet dès maintenant) ──
  async getPunctualityDays(networkId: OpaqueId, signal?: AbortSignal): Promise<string[]> {
    const r = await this.http.get<PunctualityDaysResponse>(
      `${this.base(networkId)}/punctuality/days`,
      signal ? { signal } : {},
    );
    return r.days;
  }

  getPunctualityReport(
    networkId: OpaqueId,
    date: string,
    opts: { post?: OpaqueId } = {},
    signal?: AbortSignal,
  ): Promise<PunctualityReport> {
    return this.http.get<PunctualityReport>(
      `${this.base(networkId)}/punctuality/${encodeURIComponent(date)}`,
      { query: { post: opts.post }, ...(signal ? { signal } : {}) },
    );
  }

  /** Agrégats mensuels de ponctualité (P2-35, contrat v1.17) — du plus récent au plus ancien. */
  async getPunctualityMonthly(
    networkId: OpaqueId,
    opts: { from?: string; to?: string; post?: OpaqueId } = {},
    signal?: AbortSignal,
  ): Promise<PunctualityMonthlyResponse["months"]> {
    const r = await this.http.get<PunctualityMonthlyResponse>(`${this.base(networkId)}/punctuality/monthly`, {
      query: { from: opts.from, to: opts.to, post: opts.post },
      ...(signal ? { signal } : {}),
    });
    return r.months;
  }

  /** Étendue enregistrée + cadence, pour caler le curseur du rejeu (P2-32, v1.16). */
  getReplayBounds(networkId: OpaqueId, signal?: AbortSignal): Promise<ReplayBoundsResponse> {
    return this.http.get<ReplayBoundsResponse>(`${this.base(networkId)}/replay/bounds`, signal ? { signal } : {});
  }

  /** Trames d'une fenêtre de rejeu (≤ 3 h). Mêmes filtres que la carte live. */
  getReplay(
    networkId: OpaqueId,
    query: { from: string; to: string; exploitant?: string; lineId?: OpaqueId; vehicle?: string },
    signal?: AbortSignal,
  ): Promise<ReplayResponse> {
    return this.http.get<ReplayResponse>(`${this.base(networkId)}/replay`, {
      query: {
        from: query.from,
        to: query.to,
        exploitant: query.exploitant,
        line: query.lineId,
        vehicle: query.vehicle,
      },
      ...(signal ? { signal } : {}),
    });
  }

  async getRegulatorAudit(
    networkId: OpaqueId,
    opts: { limit?: number; junctionId?: OpaqueId } = {},
    signal?: AbortSignal,
  ): Promise<RegulatorAuditResponse["entries"]> {
    const r = await this.http.get<RegulatorAuditResponse>(`${this.base(networkId)}/regulator-audit`, {
      query: { limit: opts.limit, junctionId: opts.junctionId },
      ...(signal ? { signal } : {}),
    });
    return r.entries;
  }

  getHandover(networkId: OpaqueId, opts: { post?: OpaqueId } = {}, signal?: AbortSignal): Promise<HandoverResponse> {
    return this.http.get<HandoverResponse>(`${this.base(networkId)}/handover`, {
      query: { post: opts.post },
      ...(signal ? { signal } : {}),
    });
  }

  // ── Onglet Véhicules (P2-46/47/48, contrat v1.18) ─────────────────────────
  /**
   * v1.46 (D-020) — `?post=` réintègre les flux techniques masqués par défaut
   * (`config.fleetView.hiddenOperatorIds`) si le poste les régule ;
   * `?exploitant=<flux masqué>` = demande explicite, réintègre aussi. Renvoie
   * l'enveloppe complète (pas seulement `vehicles`) : `hiddenOperatorIds` sert
   * au « N flux masqués » côté écran Véhicules.
   */
  async listVehicles(
    networkId: OpaqueId,
    opts: { exploitant?: string; post?: OpaqueId } = {},
    signal?: AbortSignal,
  ): Promise<VehiclesResponse> {
    return this.http.get<VehiclesResponse>(`${this.base(networkId)}/vehicles`, {
      query: { exploitant: opts.exploitant, post: opts.post },
      ...(signal ? { signal } : {}),
    });
  }

  getVehiclesGantt(
    networkId: OpaqueId,
    opts: { date?: string; exploitant?: string; lineId?: OpaqueId } = {},
    signal?: AbortSignal,
  ): Promise<VehicleGanttResponse> {
    return this.http.get<VehicleGanttResponse>(`${this.base(networkId)}/vehicles/gantt`, {
      query: { date: opts.date, exploitant: opts.exploitant, line: opts.lineId },
      ...(signal ? { signal } : {}),
    });
  }

  getVehicleAgenda(
    networkId: OpaqueId,
    vehicleId: OpaqueId,
    opts: { from?: string; to?: string } = {},
    signal?: AbortSignal,
  ): Promise<VehicleAgendaResponse> {
    return this.http.get<VehicleAgendaResponse>(
      `${this.base(networkId)}/vehicles/${encodeURIComponent(vehicleId)}/agenda`,
      { query: { from: opts.from, to: opts.to }, ...(signal ? { signal } : {}) },
    );
  }

  // ── Onglet Services (P2-50 / P2-51 / P2-52, contrat v1.22) ────────────────
  /** Services conducteurs actifs à l'instant (recoupement temps réel). */
  getActiveServices(networkId: OpaqueId, signal?: AbortSignal): Promise<ActiveServicesResponse> {
    return this.http.get<ActiveServicesResponse>(`${this.base(networkId)}/services`, signal ? { signal } : {});
  }

  /** Plan chronologique d'un service (Gantt + trous qualifiés) pour une date. */
  getServicePlan(
    networkId: OpaqueId,
    group: string,
    serviceNumber: string,
    opts: { date?: string } = {},
    signal?: AbortSignal,
  ): Promise<ServicePlanResponse> {
    return this.http.get<ServicePlanResponse>(
      `${this.base(networkId)}/services/${encodeURIComponent(group)}/${encodeURIComponent(serviceNumber)}`,
      { query: { date: opts.date }, ...(signal ? { signal } : {}) },
    );
  }

  /** Vue d'ensemble d'une journée (bandeaux empilés, P2-51). `date` absent = aujourd'hui. */
  getServicesTimeline(
    networkId: OpaqueId,
    opts: { date?: string; group?: string; from?: number; to?: number } = {},
    signal?: AbortSignal,
  ): Promise<ServicesTimelineResponse> {
    return this.http.get<ServicesTimelineResponse>(`${this.base(networkId)}/services/timeline`, {
      query: { date: opts.date, group: opts.group, from: opts.from, to: opts.to },
      ...(signal ? { signal } : {}),
    });
  }

  /** Véhicules dont le service se termine bientôt — « qui va se libérer » (P2-15). `within` en minutes (5..360, défaut 60). */
  getEndOfService(
    networkId: OpaqueId,
    opts: { within?: number; bbox?: readonly [number, number, number, number] } = {},
    signal?: AbortSignal,
  ): Promise<EndOfServiceResponse> {
    return this.http.get<EndOfServiceResponse>(`${this.base(networkId)}/end-of-service`, {
      query: { within: opts.within, bbox: opts.bbox ? opts.bbox.join(",") : undefined },
      ...(signal ? { signal } : {}),
    });
  }

  /**
   * Moteur d'anticipation — « Aide à la décision » (contrat v1.44). Événements
   * déjà triés (priorité `high` d'abord, puis plus ancien). CONSULTATIF : aucun
   * verbe mutant, aucun acquittement.
   */
  getAnticipation(
    networkId: OpaqueId,
    opts: { post?: OpaqueId; lineId?: OpaqueId } = {},
    signal?: AbortSignal,
  ): Promise<AnticipationResponse> {
    return this.http.get<AnticipationResponse>(`${this.base(networkId)}/anticipation`, {
      query: { post: opts.post, line: opts.lineId },
      ...(signal ? { signal } : {}),
    });
  }

  /**
   * Répartition des sorties (contrat v1.45). `target` → mode ciblé (donneurs
   * classés pour cette sortie) ; sans `target` → balayage. CONSULTATIF : aucun
   * bouton « Affecter ».
   */
  getSortieReallocation(
    networkId: OpaqueId,
    opts: { target?: string; post?: OpaqueId } = {},
    signal?: AbortSignal,
  ): Promise<SortieReallocationResponse> {
    return this.http.get<SortieReallocationResponse>(`${this.base(networkId)}/sortie-reallocation`, {
      query: { target: opts.target, post: opts.post },
      ...(signal ? { signal } : {}),
    });
  }
}

export type { Deviation, Journey };
